import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import { Coordinate, TileSize } from "../../models"
import type { TileMapMetaProvider } from "../../tile_map_meta_provider"
import { stairLandingIsSupported, stairTileAt } from "./stairs"
import { companionFloorOffset, houseRoofOffset } from "./two_storey_house"

/** Where the roof a ladder stands against lies, seen from the ladder: north is a ladder in front of a house. */
export type LadderBesideSide = "north" | "east" | "west"

/**
 * What the top of a ladder leads to (openspec movement/ladders). `through`: a roof or the floor of the storey
 * above covers its own cell, and it climbs up through a hatch there. `beside`: it stands against a roof on one
 * side, and its top steps onto `landing`, a hip location on the storey above. `bare`: neither, and a climb tops
 * out on its rungs.
 */
export type LadderTop =
    | { kind: "through" }
    | { kind: "beside"; side: LadderBesideSide; landing: Coordinate }
    | { kind: "bare" }

// Tried in this order: a ladder in a notch steps onto the first.
const SIDES: [LadderBesideSide, number, number][] = [["north", 0, -1], ["east", 1, 0], ["west", -1, 0]]

const covers = (map: TileMapMetaProvider, layer: "map2" | "sky", x: number, y: number): boolean => {
    const tile = map.resolveTileMapFromLayerId(layer)?.[x]?.[y]
    return !!tile && tile !== "0" && !map.isHiddenTile(tile) && !map.getMapDeltaAt(x, y, layer)?.removed &&
        (layer !== "map2" || !map.isFloorOpeningAt(x, y))
}

/** The floor of the storey above, or else the roof of the storey below it, on cell (x, y). */
const coverAt = (lower: TileMapMetaProvider | undefined, upper: TileMapMetaProvider, x: number, y: number) =>
    covers(upper, "map2", x, y) ? "floor" as const : lower && covers(lower, "sky", x, y) ? "roof" as const : undefined

/**
 * Where a body stands on the middle of `cell` of `upper`, as a hip location. A floor holds the soles on its own cell;
 * a roof holds them at its drawn height, a few pixels off its cell (TileMapMetaProvider.isUnsupportedFloorAtPosition).
 */
const standingOn = (lower: TileMapMetaProvider | undefined, upper: TileMapMetaProvider, cell: Coordinate,
    cover: "floor" | "roof"): Coordinate => {
    const house = upper.twoStoreyHouse
    const lowerHouse = lower?.twoStoreyHouse ?? (house && { ...house, floor: house.floor - 1 })
    const offset = cover === "roof" && house ? companionFloorOffset(house, lowerHouse) + houseRoofOffset(lowerHouse) : 0
    return { x: (cell.x + .5) * TileSize, y: (cell.y + .5) * TileSize + offset - BODY_GROUND_OFFSET }
}

/** Where a body stands on `cell` of `upper`, as a hip location, when a floor or the roof below covers it. */
export const standingSpotAt = (lower: TileMapMetaProvider | undefined, upper: TileMapMetaProvider,
    cell: Coordinate): Coordinate | undefined => {
    const cover = coverAt(lower, upper, cell.x, cell.y)
    return cover && standingOn(lower, upper, cell, cover)
}

/** What the ladder on `at` leads to, from storey `lower` (undefined: not loaded) to `upper`. */
export const ladderTopAt = (lower: TileMapMetaProvider | undefined, upper: TileMapMetaProvider, at: Coordinate): LadderTop => {
    // An explicitly connected shaft is a run of stacked ladders, each through the one above.
    const portal = upper.getMapDeltaAt(at.x, at.y, "sprite2")?.context
    const connections = portal?.context?.stairLandings ?? portal?.stairLandings
    if (lower && connections?.down?.roomId === lower.roomId && stairTileAt(upper, at, "down")) return { kind: "through" }
    if (coverAt(lower, upper, at.x, at.y)) return { kind: "through" }
    for (const [side, dx, dy] of SIDES) {
        const cell = { x: at.x + dx, y: at.y + dy }
        const cover = coverAt(lower, upper, cell.x, cell.y)
        if (!cover) continue
        const landing = standingOn(lower, upper, cell, cover)
        if (stairLandingIsSupported(upper, { ...landing, roomId: upper.roomId })) return { kind: "beside", side, landing }
    }
    return { kind: "bare" }
}

/**
 * Whether soles at `feet` on storey `map` are held up only by the top of a ladder that goes through nothing, against a
 * roof or in the open. No hatch is drawn there, so it is air: that ladder is got onto from its rungs or from the roof
 * beside its top, never by walking onto it (openspec movement/ladders). The ladder's top cell still holds the stair
 * that leads down, so a climb's own checks (stairLandingIsSupported) are unchanged.
 */
export const standsOnLadderTopInAir = (map: TileMapMetaProvider, feet: Coordinate): boolean => {
    const house = map.twoStoreyHouse
    if (!house || house.floor <= 0) return false
    const at = { x: Math.floor(feet.x / TileSize), y: Math.floor((feet.y - BODY_GROUND_OFFSET) / TileSize) }
    if (!map.isStairAt(at.x, at.y) || map.resolveTileMeta(map.resolveTileMapFromLayerId("sprite2")[at.x][at.y]).entityTileId !== "stairs-down")
        return false
    const delta = map.getMapDeltaAt(at.x, at.y, "sprite2")
    if (delta?.context?.context?.stairLandings ?? delta?.context?.stairLandings) return false
    return ladderTopAt(map.getFloorSupportProvider(), map, at).kind !== "through" && map.isUnsupportedFloorAtPosition(feet, true)
}

/**
 * The top of a ladder's rungs on the storey above, as a hip location: where a climber perched there is drawn.
 * The rungs (`rise`) are taller than the storey (`storeyRise`), so it is north of the ladder's cell centre `base`.
 */
export const ladderRungTop = (base: Coordinate, rise: number, storeyRise: number): Coordinate =>
    ({ x: base.x, y: base.y - (rise - storeyRise) })
